import { z } from 'zod'
import { add, cents, sub, sum, ZERO, type Cents } from './money.ts'

/**
 * Giving rules shared by the API, the jobs worker and both clients.
 *
 * The invariants this module exists to keep:
 *
 *  - a gift's line items sum to the gift, to the cent;
 *  - a deposit reconciles when the gifts in it, net of fees and refunds, sum
 *    to what the bank received, to the cent;
 *  - a recurring gift's next date is computed, never accumulated (adding 30
 *    days twelve times is not "monthly");
 *  - a statement says exactly what the IRS requires and nothing it does not.
 */

// ---- vocabulary ---------------------------------------------------------------------

export const GIFT_METHODS = ['card', 'ach', 'cash', 'check', 'other'] as const
export type GiftMethod = (typeof GIFT_METHODS)[number]

export const GIFT_SOURCES = ['online', 'recurring', 'batch', 'text'] as const
export type GiftSource = (typeof GIFT_SOURCES)[number]

/**
 * Who the money came from, as far as a tax statement cares (Planning
 * Center's payment sources):
 *
 *  - `direct` — from the donor;
 *  - `daf` — a donor-advised fund grant: the donor deducted it when they
 *    funded the DAF, so it is not theirs to deduct again;
 *  - `qcd` — a qualified charitable distribution from an IRA: excluded from
 *    the donor's income instead of deducted;
 *  - `third_party` — another giving platform passing the donor's own gift on.
 *
 * DAF and QCD gifts are real gifts in every total but a donor's statement.
 */
export const PAYMENT_SOURCES = ['direct', 'daf', 'qcd', 'third_party'] as const
export type PaymentSource = (typeof PAYMENT_SOURCES)[number]

export const PAYMENT_SOURCE_LABELS: Record<PaymentSource, string> = {
  direct: 'From the donor',
  daf: 'Donor-advised fund',
  qcd: 'IRA distribution (QCD)',
  third_party: 'Another giving platform',
}

/** Does a gift from this source belong on the donor's tax statement? */
export const onStatement = (source: PaymentSource): boolean =>
  source === 'direct' || source === 'third_party'

export interface StatementLine {
  amount: Cents
  source: PaymentSource
  fundDeductible: boolean
}

/**
 * What a donor's statement totals: deductible funds, deductible sources, net
 * of refunds (a refund carries its gift's source, so a refunded DAF gift
 * cancels where it was counted — nowhere).
 */
export function statementTotal(lines: readonly StatementLine[]): Cents {
  return sum(
    lines.filter((line) => line.fundDeductible && onStatement(line.source)).map((l) => l.amount),
  )
}

/**
 * Labels as staff type them: trimmed, case-insensitively unique (the first
 * spelling wins), at most 10 of at most 40 characters.
 */
export function normaliseDonationLabels(labels: readonly string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of labels) {
    const label = raw.trim().replace(/\s+/g, ' ').slice(0, 40)
    const key = label.toLowerCase()
    if (!label || seen.has(key)) continue
    seen.add(key)
    out.push(label)
    if (out.length === 10) break
  }
  return out
}

export const GIFT_FREQUENCIES = ['weekly', 'biweekly', 'monthly', 'quarterly', 'yearly'] as const
export type GiftFrequency = (typeof GIFT_FREQUENCIES)[number]

export const FREQUENCY_LABELS: Record<GiftFrequency, string> = {
  weekly: 'Every week',
  biweekly: 'Every two weeks',
  monthly: 'Every month',
  quarterly: 'Every three months',
  yearly: 'Every year',
}

// ---- recurring schedules --------------------------------------------------------------

const toDate = (iso: string) => new Date(`${iso}T00:00:00Z`)
const toIso = (date: Date) => date.toISOString().slice(0, 10)
const daysInMonth = (year: number, month: number) =>
  new Date(Date.UTC(year, month + 1, 0)).getUTCDate()

/**
 * The charge date `index` periods after `start`.
 *
 * Month-based frequencies keep the *day of the month* of the first gift,
 * clamped to the month's length — a gift started on the 31st is charged on
 * the 30th in April and the 28th or 29th in February, and back on the 31st in
 * March. Computing from the start every time (not from the previous charge)
 * is what stops "the 31st" from becoming "the 28th forever" after February.
 */
export function chargeDate(start: string, frequency: GiftFrequency, index: number): string {
  if (!Number.isSafeInteger(index) || index < 0) throw new RangeError('index must be ≥ 0')
  const first = toDate(start)
  if (frequency === 'weekly' || frequency === 'biweekly') {
    const step = frequency === 'weekly' ? 7 : 14
    return toIso(new Date(first.getTime() + index * step * 86_400_000))
  }
  const months = { monthly: 1, quarterly: 3, yearly: 12 }[frequency] * index
  const year = first.getUTCFullYear() + Math.floor((first.getUTCMonth() + months) / 12)
  const month = (first.getUTCMonth() + months) % 12
  const day = Math.min(first.getUTCDate(), daysInMonth(year, month))
  return toIso(new Date(Date.UTC(year, month, day)))
}

/** The first charge date on or after `onOrAfter`, and its index. */
export function nextChargeOnOrAfter(
  start: string,
  frequency: GiftFrequency,
  onOrAfter: string,
): { date: string; index: number } {
  // A bounded walk: a century of weekly gifts is ~5,200 steps.
  for (let index = 0; index < 6_000; index++) {
    const date = chargeDate(start, frequency, index)
    if (date >= onOrAfter) return { date, index }
  }
  throw new RangeError('No charge date within a century')
}

/** The next `count` charge dates on or after `from`. */
export function projectSchedule(
  start: string,
  frequency: GiftFrequency,
  from: string,
  count: number,
): string[] {
  const first = nextChargeOnOrAfter(start, frequency, from)
  return Array.from({ length: count }, (_, i) => chargeDate(start, frequency, first.index + i))
}

// ---- multi-fund gifts ----------------------------------------------------------------

export interface Allocation {
  fundId: string
  amount: Cents
}

/** A gift's designations must name distinct funds and add up to the gift. */
export function checkAllocations(total: Cents, allocations: readonly Allocation[]): string | null {
  if (allocations.length === 0) return 'Choose at least one fund.'
  if (new Set(allocations.map((a) => a.fundId)).size !== allocations.length) {
    return 'Each fund can appear once in a gift.'
  }
  if (allocations.some((a) => a.amount <= 0)) return 'Every designation must be more than zero.'
  if (sum(allocations.map((a) => a.amount)) !== total) {
    return 'The designations must add up to the gift exactly.'
  }
  return null
}

// ---- pledges ---------------------------------------------------------------------------

export interface PledgeProgress {
  given: Cents
  remaining: Cents
  /** 0–100, whole percent, floored: 99.6% given is not "100%". */
  percent: number
  /** What a straight-line pace would have given by `today`. */
  expectedByNow: Cents
  onTrack: boolean
}

export function pledgeProgress(input: {
  pledged: Cents
  given: Cents
  startsOn: string
  /** null ⇒ an open-ended campaign: no pace to be behind. */
  endsOn: string | null
  today: string
}): PledgeProgress {
  const remaining = input.given >= input.pledged ? ZERO : sub(input.pledged, input.given)
  const percent =
    input.pledged <= 0 ? 100 : Math.min(100, Math.floor((input.given * 100) / input.pledged))
  if (input.endsOn === null) {
    return { given: input.given, remaining, percent, expectedByNow: ZERO, onTrack: true }
  }
  const span = toDate(input.endsOn).getTime() - toDate(input.startsOn).getTime()
  const elapsed = Math.min(
    Math.max(toDate(input.today).getTime() - toDate(input.startsOn).getTime(), 0),
    Math.max(span, 0),
  )
  const expectedByNow =
    span <= 0 ? input.pledged : cents(Math.floor((input.pledged * elapsed) / span))
  return {
    given: input.given,
    remaining,
    percent,
    expectedByNow,
    onTrack: input.given >= expectedByNow,
  }
}

// ---- reconciliation ----------------------------------------------------------------------

export interface SettledGift {
  /** Signed: a refund settles as a negative gross. */
  gross: Cents
  fee: Cents
}

/**
 * What a payout should be: every gift in it, gross less fees. A refund is a
 * negative gross (and usually a zero fee — processors keep the original fee).
 * `difference` is actual − expected; zero is the only reconciled answer.
 */
export function reconcile(
  deposit: Cents,
  gifts: readonly SettledGift[],
): { expected: Cents; difference: Cents; reconciled: boolean } {
  const expected = gifts.reduce<Cents>((acc, gift) => add(acc, sub(gift.gross, gift.fee)), ZERO)
  const difference = sub(deposit, expected)
  return { expected, difference, reconciled: difference === 0 }
}

// ---- statements ----------------------------------------------------------------------------

/**
 * IRS §170(f)(8): a contemporaneous written acknowledgment must state the
 * amount, and whether the organization provided goods or services in return.
 * For ordinary gifts the answer is no, and the statement must say so.
 */
export const NO_GOODS_OR_SERVICES =
  'No goods or services were provided in exchange for these contributions, other than intangible religious benefits.'

export interface StatementSnapshot {
  taxYear: number
  church: { name: string; address: string | null; ein: string | null }
  donor: { name: string; address: string | null }
  gifts: { date: string; fund: string; amount: Cents; method: string }[]
  total: Cents
  /**
   * Gifts of goods or services: described, never valued — the value is the
   * donor's to establish. Absent on statements issued before in-kind gifts
   * were recorded, which keeps their hashes.
   */
  nonCash?: { date: string; description: string }[]
  /** When the run was taken. Fixed in the snapshot so a regeneration matches. */
  issuedOn: string
}

// ---- card numbers ---------------------------------------------------------------------------

/** Luhn checksum over a string of digits. */
export function luhnValid(digits: string): boolean {
  let total = 0
  let double = false
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = digits.charCodeAt(i) - 48
    if (digit < 0 || digit > 9) return false
    if (double) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    total += digit
    double = !double
  }
  return digits.length > 0 && total % 10 === 0
}

/**
 * Does this text contain something shaped like a payment card number?
 *
 * The shapes a card number is actually written in — 13–19 digits run
 * together, or grouped in fours (4-4-4-4, with a short tail for 19-digit
 * cards), or Amex's 4-6-5 — standing on their own, not inside a longer token,
 * and passing the Luhn check. "On their own" is what keeps identifiers out: a
 * UUID's last two segments (`8123-456789012345`) are digits and a dash, but
 * they sit inside a hex string, and the old looser pattern flagged them.
 *
 * Used to refuse request bodies that carry one (card data must never reach
 * our servers — SAQ-A) and by the scan that proves none is stored.
 */
const CARD_SHAPES =
  /(?<![\w.-])(?:\d{13,19}|\d{4}(?:[ -]\d{4}){2,3}(?:[ -]\d{1,3})?|\d{4}[ -]\d{6}[ -]\d{5})(?![\w-])/g

export function containsCardNumber(text: string): boolean {
  const candidates = text.match(CARD_SHAPES)
  if (!candidates) return false
  return candidates.some((candidate) => {
    const digits = candidate.replace(/[ -]/g, '')
    return digits.length >= 13 && digits.length <= 19 && luhnValid(digits)
  })
}

// ---- schemas -------------------------------------------------------------------------------

/** Recording a gift of goods or services. The estimate is staff's note, never printed. */
export const inKindGiftSchema = z.object({
  personId: z.uuid().nullish(),
  receivedOn: z.iso.date(),
  description: z.string().trim().min(1, 'Describe the gift').max(500),
  estimatedValueCents: z.number().int().positive().max(10_000_000_000).nullish(),
  fundId: z.uuid().nullish(),
  campusId: z.uuid().nullish(),
  note: z.string().trim().max(1000).nullish(),
})
export type InKindGiftInput = z.infer<typeof inKindGiftSchema>

export const fundInputSchema = z.object({
  name: z.string().trim().min(1, 'Name the fund').max(120),
  description: z.string().trim().max(500).nullish(),
  code: z.string().trim().max(40).nullish(),
  isTaxDeductible: z.boolean().default(true),
})
export type FundInput = z.infer<typeof fundInputSchema>

/**
 * The public Give form: what a visitor types, before it becomes cents. The
 * amount is text so "50" and "50.00" and "$1,200" all parse through
 * `parseCents` — never through a float.
 */
export const giveFormSchema = z.object({
  amount: z.string().trim().min(1, 'Enter an amount'),
  fundId: z.uuid('Choose a fund'),
  coverFees: z.boolean().default(false),
  method: z.enum(['card', 'ach']).default('card'),
  frequency: z.enum(['once', ...GIFT_FREQUENCIES]).default('once'),
  firstName: z.string().trim().min(1, 'First name').max(100),
  lastName: z.string().trim().min(1, 'Last name').max(100),
  email: z.email('A valid email, for your receipt'),
})
export type GiveFormInput = z.input<typeof giveFormSchema>
export type GiveForm = z.infer<typeof giveFormSchema>
